import { Component, signal, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { OauthService } from '../../services/oauth.service';
import { ToastService } from '../../services/toast.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-signup',
  imports: [FormsModule, CommonModule, RouterLink],
  templateUrl: './signup.component.html'
})
export class SignupComponent {
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  constructor(private auth: OauthService) {}

  fullName = '';
  email = '';
  password = '';
  confirmPassword = '';
  showPassword = signal(false);
  showConfirmPassword = signal(false);
  submitting = signal(false);
  /** Flipped on the first submit attempt so untouched fields still reveal their errors. */
  submitted = signal(false);

  static readonly NAME_MIN = 5;
  static readonly PASSWORD_MIN = 8;
  // Local part ≥3 chars; TLD ≥2 chars so .ai / .io still pass.
  private static readonly EMAIL_RE = /^[^\s@]{3,}@[^\s@]+\.[^\s@]{2,}$/;

  get nameError(): string | null {
    if (!this.fullName.trim()) return 'Full name is required';
    if (this.fullName.trim().length < SignupComponent.NAME_MIN)
      return `Full name must be at least ${SignupComponent.NAME_MIN} characters`;
    return null;
  }

  get emailError(): string | null {
    if (!this.email.trim()) return 'Email is required';
    if (!SignupComponent.EMAIL_RE.test(this.email.trim()))
      return 'Enter a valid email address';
    return null;
  }

  get passwordError(): string | null {
    if (!this.password) return 'Password is required';
    if (this.password.length < SignupComponent.PASSWORD_MIN)
      return `Password must be at least ${SignupComponent.PASSWORD_MIN} characters`;
    if (!/[A-Z]/.test(this.password))
      return 'Password must include an uppercase letter';
    if (!/[a-z]/.test(this.password))
      return 'Password must include a lowercase letter';
    if (!/[0-9]/.test(this.password))
      return 'Password must include a number';
    if (!/[^A-Za-z0-9]/.test(this.password))
      return 'Password must include a special character';
    return null;
  }

  get confirmError(): string | null {
    if (!this.confirmPassword) return 'Please confirm your password';
    if (this.confirmPassword !== this.password) return 'Passwords do not match';
    return null;
  }

  get formValid(): boolean {
    return (
      !this.nameError &&
      !this.emailError &&
      !this.passwordError &&
      !this.confirmError
    );
  }

  onSubmit(): void {
    if (!this.formValid) {
      const message =
        this.nameError ?? this.emailError ?? this.passwordError ?? this.confirmError;
      if (message) this.toast.showWarning(message, 'Check your details');
      return;
    }
    this.submitting.set(true);
    this.auth
      .register(this.fullName, this.email, this.password)
      .then(() => {
        this.toast.showSuccess('Account created successfully', 'Welcome');
        this.router.navigate(['/']);
      })
      .catch((err) => {
        const detail = err?.error?.detail ?? err?.message ?? 'Sign up failed';
        this.toast.showError(typeof detail === 'string' ? detail : 'Sign up failed', 'Sign up failed');
      })
      .finally(() => {
        this.submitting.set(false);
      });
  }

}